import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import type { ColumnDefinition } from "@/shared/components/CRUD/types";
import type { RevenueShareReportsListRow } from "@/admin/entities/revenueShareReports";
import { formatMoney, type MoneyLike } from "./billingFormat";
import { renderRevenueShareReportStatus } from "./billingLabels";

/**
 * Колонки отчёта доли с продаж, общие для страницы «Биллинг» и вкладки
 * организации: рендер и подписи одинаковые, различается только набор
 * столбцов вокруг них (общая страница добавляет «Организация», вкладка
 * добавляет курс и корректировки).
 */

export function MoneyAmountCell({
  amount,
  trailing,
  note,
}: {
  amount?: MoneyLike | null;
  trailing?: string;
  note?: string;
}) {
  if (!amount) return null;

  const secondary = [
    amount.currency !== "USD" && amount.valueUSD != null
      ? formatMoney({ value: amount.valueUSD, currency: "USD" })
      : "",
    note ?? "",
  ].filter(Boolean);

  return (
    <Stack spacing={0}>
      <Typography variant="body2">
        {formatMoney(amount)}
        {trailing ? `, ${trailing}` : ""}
      </Typography>
      {secondary.length > 0 && (
        <Typography variant="caption" color="textSecondary">
          {secondary.join(" · ")}
        </Typography>
      )}
    </Stack>
  );
}

export function buildPeriodColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return { name: "period", label: "Месяц", options: {} };
}

export function buildStatusColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return {
    name: "status",
    label: "Статус",
    options: {
      customBodyRender: (value: unknown) =>
        renderRevenueShareReportStatus(String(value)),
    },
  };
}

export function buildSalesColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return {
    name: "sales",
    label: "Продажи",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const report = data as RevenueShareReportsListRow;
        return (
          <MoneyAmountCell
            amount={report.salesAmount}
            trailing={`${report.purchasesCount} шт`}
          />
        );
      },
    },
  };
}

export function buildProviderFeeColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return {
    name: "providerFee",
    label: "Комиссия платёжной системы",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => (
        <MoneyAmountCell
          amount={(data as RevenueShareReportsListRow).providerFeeAmount}
        />
      ),
    },
  };
}

export function buildFeeColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return {
    name: "fee",
    label: "Наша доля",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const report = data as RevenueShareReportsListRow;
        const percentages = report.feePercentages
          .map((value) => `${value.toLocaleString("ru-RU")}%`)
          .join(" / ");
        return <MoneyAmountCell amount={report.feeAmount} note={percentages} />;
      },
    },
  };
}

export function buildTotalColumn(): ColumnDefinition<RevenueShareReportsListRow> {
  return {
    name: "total",
    label: "Итого",
    options: {
      sort: false,
      customBodyRender: (data: unknown) => {
        const report = data as RevenueShareReportsListRow;
        const contractAmount = report.reportAmountInContractCurrency;
        const contractNote =
          contractAmount &&
          contractAmount.currency !== report.reportAmount.currency &&
          contractAmount.currency !== "USD"
            ? formatMoney(contractAmount)
            : undefined;
        return (
          <MoneyAmountCell amount={report.reportAmount} note={contractNote} />
        );
      },
    },
  };
}
